import { FileTextOutlined, UploadOutlined } from "@ant-design/icons";
import { message, Upload } from "antd";
import { useContext, useEffect, useState } from "react";
import { api } from "../../../api";
import { SettingsSidebar } from "../../../externalComponents/SettingsSidebar";
import { PageHeading } from "../../../externalComponents/PageHeading";
import { CustomerContext } from "../../../providers";
import { OverflowFrame } from "../../../externalComponents";
import {
  SwitchField,
  SwitchOnOff,
} from "../../../externalComponents/tailwind/switch";
import { Text } from "../../../externalComponents/tailwind/text";
import { Input } from "../../../externalComponents/tailwind/input";
import {
  Description,
  ErrorMessage,
  Field,
  Label,
} from "../../../externalComponents/tailwind/fieldset";
import { Badge } from "../../../externalComponents/tailwind/badge";
import { Button } from "../../../externalComponents/tailwind/button";
import {
  Checkbox,
  CheckboxField,
} from "../../../externalComponents/tailwind/checkbox";
import {
  InformationCircleIcon,
  LightBulbIcon,
} from "@heroicons/react/16/solid";

const { Dragger } = Upload;

const MASKED_PASSWORD = "*".repeat(20);

// Files a customer can pick for their daily export. Keys match the customer.dailyExportFiles enum,
// headers mirror the CSVs built by buildContactsCsv and iterateTrackingExport.
const EXPORT_FILES = [
  {
    key: "messageTracking",
    label: "Message tracking",
    headers: [
      "Flow ID",
      "Flow Name",
      "Message ID",
      "Sent At",
      "Delivered At",
      "Read At",
      "Failed At",
      "Failed Reason",
      "Status",
      "Phone Number",
      "External Id",
      "First Name",
      "Last Name",
      "WhatsApp Name",
      "optinStatus",
      "optinAt",
      "optoutAt",
      "createdAt",
      "hasWhatsApp",
      "Button Title",
      "Button Clicked At",
    ],
    // Always exported for Adobe/SFMC customers
    isRequiredForIntegration: true,
  },
  {
    key: "optinTracking",
    label: "Optin tracking",
    headers: [
      "Phone Number",
      "First Name",
      "Last Name",
      "WhatsApp Name",
      "Optin Status",
      "Optin At",
      "Optout At",
      "Created At",
      "Has WhatsApp",
    ],
    isRequiredForIntegration: true,
    // Only present in Adobe/SFMC exports: one column per customer custom field
    integrationOnlyHeaders: ["Custom fields"],
  },
];

const EXPORT_FILE_KEYS = EXPORT_FILES.map((file) => file.key);

// Thrown when inline field errors are displayed, so the page doesn't also show a toast
class ValidationError extends Error {}

function ScheduleHint({ children }) {
  return (
    <div className="flex items-center mb-5">
      <LightBulbIcon className="h-4 w-4 mr-2" />{" "}
      <Text as="span" className="text-sm">
        {children}
      </Text>
    </div>
  );
}

function ExportFileCard({ file, headers, checked, disabled, onChange }) {
  return (
    // Only the checkbox is disabled: dimming the whole card would make a locked export look inactive
    <CheckboxField
      className={`rounded-lg border p-4 ${
        checked ? "border-pink" : "border-zinc-950/10 dark:border-white/10"
      }`}
    >
      <Checkbox
        name={`export_file_${file.key}`}
        color="pink"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <Label>{file.label}</Label>
      <Description className="font-mono !text-xs">
        {headers.join(" · ")}
      </Description>
    </CheckboxField>
  );
}

function useSftpExportTab({ customer }) {
  const [isSftpExportChecked, setIsSftpExportChecked] = useState(
    customer.isExportSftpActivated ?? false,
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isS3Export, setIsS3Export] = useState(false);
  const [isManagedByIntegration, setIsManagedByIntegration] = useState(false);
  const [integrationLabel, setIntegrationLabel] = useState("");

  const [host, setHost] = useState("");
  const [port, setPort] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [uploadDirectory, setUploadDirectory] = useState("");
  const [hasPrivateKey, setHasPrivateKey] = useState(false);
  const [hasPrivateKeyPassphrase, setHasPrivateKeyPassphrase] = useState(false);
  const [privateKeyFileName, setPrivateKeyFileName] = useState("");
  const [privateKeyBase64, setPrivateKeyBase64] = useState("");
  const [privateKeyPassphrase, setPrivateKeyPassphrase] = useState("");
  const [exportFiles, setExportFiles] = useState([]);
  const [errors, setErrors] = useState({});

  // Always prefill: the connector may exist even when the self-service export is off
  // (Adobe/SFMC customers, or a customer who disabled the export earlier)
  useEffect(() => {
    if (isConfigLoaded) {
      return;
    }
    (async () => {
      try {
        const config = await api.getMyExportSftpConnectorConfig();
        setIsS3Export(config.isS3Export || false);
        setIsManagedByIntegration(config.isManagedByIntegration || false);
        setIntegrationLabel(config.integrationLabel || "");
        if (config.isManagedByIntegration && !config.isS3Export) {
          setIsSftpExportChecked(true);
        }
        setExportFiles(config.exportFiles || []);
        setHost(config.host || "");
        setPort(config.port || "");
        setUsername(config.username || "");
        setUploadDirectory(config.uploadDirectory || "");
        setPassword(config.hasPassword ? MASKED_PASSWORD : "");
        setHasPrivateKey(config.hasPrivateKey || false);
        setHasPrivateKeyPassphrase(config.hasPrivateKeyPassphrase || false);
      } catch (error) {
        message.error(
          `Failed to load the SFTP configuration: ${error.message}`,
        );
      } finally {
        setIsConfigLoaded(true);
      }
    })();
  }, [isConfigLoaded]);

  // Files required by Adobe/SFMC can't be unchecked
  const isExportFileLocked = (file) =>
    isManagedByIntegration && file.isRequiredForIntegration;

  const getExportFileHeaders = (file) =>
    isManagedByIntegration && file.integrationOnlyHeaders
      ? [...file.headers, ...file.integrationOnlyHeaders]
      : file.headers;

  const toggleExportFile = (key, checked) => {
    setIsEditing(true);
    setExportFiles((current) =>
      EXPORT_FILE_KEYS.filter((k) =>
        k === key ? checked : current.includes(k),
      ),
    );
  };

  const handlePrivateKeyUpload = ({ file, onSuccess, onError }) => {
    const reader = new FileReader();
    reader.onload = () => {
      setIsEditing(true);
      setPrivateKeyFileName(file.name);
      setPrivateKeyBase64(String(reader.result).split(",")[1] || "");
      onSuccess();
    };
    reader.onerror = () => {
      message.error("Failed to read the private key file");
      onError(new Error("Failed to read file"));
    };
    reader.readAsDataURL(file);
  };

  const validate = ({ requireExportFiles = true } = {}) => {
    const nextErrors = {};
    if (!host.trim()) nextErrors.host = "Host is required";
    if (!username.trim()) nextErrors.username = "Username is required";
    if (!uploadDirectory.trim())
      nextErrors.uploadDirectory = "Remote directory is required";
    if (!password && !privateKeyBase64 && !hasPrivateKey)
      nextErrors.auth = "Provide a password or upload a private key";
    if (requireExportFiles && exportFiles.length === 0)
      nextErrors.exportFiles = "Select at least one file to export";

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      throw new ValidationError();
    }
  };

  // Secrets already on file are never sent back: omitting them tells the backend to keep them
  const buildConnectionPayload = () => ({
    host: host.trim(),
    port: port.trim(),
    username: username.trim(),
    uploadDirectory: uploadDirectory.trim(),
    ...(password && password !== MASKED_PASSWORD ? { password } : {}),
    ...(privateKeyBase64 ? { privateKey: privateKeyBase64 } : {}),
    ...(privateKeyPassphrase ? { privateKeyPassphrase } : {}),
  });

  const save = async () => {
    if (isS3Export) {
      return;
    }

    if (!isSftpExportChecked) {
      setErrors({});
      await api.saveMyExportSftpSettings({ enabled: false });
      setIsEditing(false);
      return;
    }

    validate();

    await api.saveMyExportSftpSettings({
      enabled: true,
      ...buildConnectionPayload(),
      // Files required by Adobe/SFMC can't be unchecked here, and the backend enforces them anyway
      exportFiles,
    });
    if (privateKeyBase64) {
      setHasPrivateKey(true);
    }
    if (privateKeyPassphrase) {
      setHasPrivateKeyPassphrase(true);
    }
    setIsEditing(false);
  };

  const testConnection = async () => {
    try {
      validate({ requireExportFiles: false });
    } catch {
      return;
    }
    setIsTesting(true);
    try {
      const result = await api.testMyExportSftpConnection(
        buildConnectionPayload(),
      );
      if (result.success) {
        message.success(result.message);
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error(`Connection test failed: ${error.message}`);
    } finally {
      setIsTesting(false);
    }
  };

  const content = (
    <>
      <ScheduleHint>
        Contacts are exported every day at 4:00 UTC, and message tracking at
        5:00 UTC
      </ScheduleHint>
      <div>
        <div>
          {isS3Export && (
            <Badge color="amber" className="mb-3">
              <InformationCircleIcon className="w-4 h-4" />
              <Text className="!text-xs">
                Your data is exported to an S3 bucket, so an SFTP export can't
                be configured. Please contact support to change it.
              </Text>
            </Badge>
          )}
          <div className="min-w-[22rem] flex content-center items-center">
            <SwitchField>
              <Label className="font-bold">Export to SFTP</Label>
              <SwitchOnOff
                name="activate_export_sftp"
                checked={isSftpExportChecked && !isS3Export}
                disabled={isManagedByIntegration || isS3Export}
                onChange={(checked) => {
                  setIsSftpExportChecked(checked);
                  setIsEditing(true);
                }}
              ></SwitchOnOff>
            </SwitchField>
            {isEditing && (
              <Text className="text-pink text-xs ml-3">
                Unsaved settings changes
              </Text>
            )}
          </div>
          {!isS3Export && isManagedByIntegration && (
            <Text className="text-sm text-zinc-500 mt-1">
              This export is managed by your {integrationLabel} integration.
              You can update the connection details below.
            </Text>
          )}
        </div>

        {(isSftpExportChecked || isS3Export) && (
          <div className="mt-4 flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field>
                <Label>Host</Label>
                <Input
                  name="SFTP host"
                  disabled={isS3Export}
                  value={host}
                  onChange={(e) => {
                    setIsEditing(true);
                    setHost(e.target.value);
                  }}
                  invalid={!!errors.host}
                />
                {errors.host && <ErrorMessage>{errors.host}</ErrorMessage>}
              </Field>
              <Field>
                <Label>Port</Label>
                <Input
                  name="SFTP port"
                  disabled={isS3Export}
                  value={port}
                  placeholder="22"
                  onChange={(e) => {
                    setIsEditing(true);
                    setPort(e.target.value);
                  }}
                />
              </Field>
              <Field>
                <Label>Username</Label>
                <Input
                  name="SFTP username"
                  disabled={isS3Export}
                  value={username}
                  onChange={(e) => {
                    setIsEditing(true);
                    setUsername(e.target.value);
                  }}
                  invalid={!!errors.username}
                />
                {errors.username && (
                  <ErrorMessage>{errors.username}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Password</Label>
                <Input
                  name="SFTP password"
                  disabled={isS3Export}
                  type="password"
                  autoComplete="off"
                  value={password}
                  onChange={(e) => {
                    setIsEditing(true);
                    setPassword(e.target.value);
                  }}
                  invalid={!!errors.auth}
                />
              </Field>
              <Field className="sm:col-span-2">
                <Label>Remote directory</Label>
                <Input
                  name="SFTP remote directory"
                  disabled={isS3Export}
                  value={uploadDirectory}
                  onChange={(e) => {
                    setIsEditing(true);
                    setUploadDirectory(e.target.value);
                  }}
                  invalid={!!errors.uploadDirectory}
                />
                {errors.uploadDirectory && (
                  <ErrorMessage>{errors.uploadDirectory}</ErrorMessage>
                )}
              </Field>
            </div>

            <div className="border-t border-zinc-950/10 dark:border-white/10 pt-4">
              <Text className="font-bold">Private key (optional)</Text>
              <Text className="text-sm text-zinc-500">
                Alternative to the password above — only needed if your SFTP
                server requires key-based authentication instead of a password.
              </Text>
              {hasPrivateKey && !privateKeyFileName && (
                <Text className="text-sm text-green-600 mt-2">
                  A private key is currently configured. Upload a file below to
                  replace it, or leave this alone to keep it.
                </Text>
              )}
              <div className="mt-2 [&_.ant-upload-btn]:!py-2">
                <Dragger
                  name="privateKey"
                  showUploadList={false}
                  maxCount={1}
                  multiple={false}
                  disabled={isS3Export}
                  customRequest={handlePrivateKeyUpload}
                >
                  <div className="flex items-center justify-center gap-2 px-3 text-sm text-zinc-600">
                    {privateKeyFileName ? (
                      <FileTextOutlined />
                    ) : (
                      <UploadOutlined />
                    )}
                    <span className="truncate">
                      {privateKeyFileName ||
                        "Click or drag-and-drop a private key file"}
                    </span>
                  </div>
                </Dragger>
              </div>
              <Field className="mt-3">
                <Label>Private key passphrase (optional)</Label>
                <Input
                  name="SFTP private key passphrase"
                  disabled={isS3Export}
                  type="password"
                  autoComplete="off"
                  value={privateKeyPassphrase}
                  placeholder={
                    hasPrivateKeyPassphrase
                      ? "A passphrase is configured, type a new one to replace it"
                      : ""
                  }
                  onChange={(e) => {
                    setIsEditing(true);
                    setPrivateKeyPassphrase(e.target.value);
                  }}
                />
              </Field>
            </div>

            {errors.auth && (
              <Field>
                <ErrorMessage>{errors.auth}</ErrorMessage>
              </Field>
            )}

            <div>
              <Button
                color="secondaryButton"
                loading={isTesting}
                disabled={isS3Export}
                onClick={testConnection}
              >
                Test connection
              </Button>
            </div>

            <div className="border-t border-zinc-950/10 dark:border-white/10 pt-4">
              <Text className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                Files to export
              </Text>
              {isManagedByIntegration && (
                <Text className="text-sm text-zinc-500 mt-1">
                  These files are exported automatically because of your{" "}
                  {integrationLabel} integration.
                </Text>
              )}
              <div className="mt-2 flex flex-col gap-3">
                {EXPORT_FILES.map((file) => (
                  <ExportFileCard
                    key={file.key}
                    file={file}
                    headers={getExportFileHeaders(file)}
                    checked={
                      isExportFileLocked(file) || exportFiles.includes(file.key)
                    }
                    disabled={isExportFileLocked(file)}
                    onChange={(checked) => toggleExportFile(file.key, checked)}
                  />
                ))}
              </div>
              {errors.exportFiles && (
                <Field>
                  <ErrorMessage>{errors.exportFiles}</ErrorMessage>
                </Field>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );

  return { save, isDirty: isEditing, content };
}

function useGoogleSheetExportTab({ customer }) {
  const [isExportDataChecked, setIsExportDataChecked] = useState(
    customer.isExportCampaignsDataGoogleSheetActivated ?? false,
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isUrlMissing, setIsUrlMissing] = useState(false);
  const [spreadsheetUrl, setSpreadsheetUrl] = useState(
    customer.campaignsDataSpreadsheetUrl || "",
  );

  const save = async () => {
    if (isExportDataChecked && spreadsheetUrl === "") {
      setIsUrlMissing(true);
      throw new ValidationError();
    }
    setIsUrlMissing(false);
    await api.customer.update(customer.id, {
      isExportCampaignsDataGoogleSheetActivated: isExportDataChecked,
      campaignsDataSpreadsheetUrl: spreadsheetUrl,
    });
    setIsEditing(false);
  };

  return {
    save,
    isDirty: isEditing,
    content: (
      <>
        <ScheduleHint>Data is exported every day at 3:00 UTC</ScheduleHint>
        <Badge color="amber">
          <InformationCircleIcon className="w-5 h-5" />
          <Text>Invite</Text>
          <Text className="font-bold">
            gadget@simio-434617.iam.gserviceaccount.com
          </Text>
          <Text>with editing rights to the spreadsheet</Text>
        </Badge>
        <Field>
          <div className="mt-5">
            <div className="min-w-[22rem] flex content-center items-center">
              <SwitchField className="">
                <Label className="font-bold">Export daily performances</Label>
                <SwitchOnOff
                  name="activate_export_daily_performances"
                  checked={isExportDataChecked}
                  onChange={(checked) => {
                    setIsExportDataChecked(checked);
                    setIsEditing(true);
                  }}
                ></SwitchOnOff>
              </SwitchField>
              {isEditing && (
                <Text className="text-pink text-xs ml-3">
                  Unsaved settings changes
                </Text>
              )}
            </div>
          </div>

          {isExportDataChecked && (
            <div className="mt-4">
              <Label>Google Spreadsheet URL</Label>
              <Input
                name="Spreadsheet url field"
                value={spreadsheetUrl}
                onChange={(e) => {
                  setIsEditing(true);
                  setSpreadsheetUrl(e.target.value);
                }}
                invalid={isUrlMissing}
              />
            </div>
          )}
          {isUrlMissing && (
            <Field>
              <ErrorMessage>Url is required</ErrorMessage>
            </Field>
          )}
        </Field>
      </>
    ),
  };
}

const TABS = [
  { id: "sftp", label: "SFTP" },
  { id: "googleSheet", label: "Google Sheet" },
];

export default function DataExportsPage() {
  const { customer, refreshCustomer } = useContext(CustomerContext);
  const [activeTab, setActiveTab] = useState("sftp");
  const [isLoading, setIsLoading] = useState(false);

  const tabs = {
    sftp: useSftpExportTab({ customer }),
    googleSheet: useGoogleSheetExportTab({ customer }),
  };

  const handlePrimaryAction = async () => {
    // Save the visible tab plus any other tab with unsaved changes, so nothing is silently lost
    const tabIdsToSave = TABS.map((tab) => tab.id).filter(
      (id) => id === activeTab || tabs[id].isDirty,
    );

    setIsLoading(true);
    try {
      for (const id of tabIdsToSave) {
        try {
          await tabs[id].save();
        } catch (error) {
          setActiveTab(id);
          if (!(error instanceof ValidationError)) {
            message.error(`Failed to save export settings: ${error.message}`);
          }
          return;
        }
      }
      message.success("Export settings saved");
      await refreshCustomer?.();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SettingsSidebar>
      <PageHeading
        title="Exports"
        primaryAction="Save"
        handlePrimaryAction={handlePrimaryAction}
        isPrimaryLoading={isLoading}
      />
      <OverflowFrame>
        <div className="mt-5 pb-10 max-w-4xl">
          <Text className="font-bold">
            Configure automatic daily export of your data.
          </Text>

          <div className="flex gap-2 mt-5 border-b border-zinc-950/10 dark:border-white/10 pb-3">
            {TABS.map((tab) => (
              <Button
                key={tab.id}
                color={
                  activeTab === tab.id ? "primaryButton" : "secondaryButton"
                }
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </Button>
            ))}
          </div>

          <div className="mt-5">{tabs[activeTab].content}</div>
        </div>
      </OverflowFrame>
    </SettingsSidebar>
  );
}
